import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BookOpen } from "lucide-react";
import type { ReactElement } from "react";
import { useEffect } from "react";
import { AuthRequired } from "../components/AuthRequired";
import { StoryLoadingState } from "../components/StoryLoadingState";
import { Logo } from "../components/ui/Logo";
import { useAuth } from "../contexts/AuthContext";
import { apiClient } from "../lib/api";

export const Route = createFileRoute("/story/$storyId/print")({
    component: StoryPrintRoute,
});

function StoryPrintRoute(): ReactElement {
    const { storyId } = Route.useParams();
    const { user, loading } = useAuth();

    // Keyboard shortcuts for better UX
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ctrl+P or Cmd+P to print
            if ((e.ctrlKey || e.metaKey) && e.key === "p") {
                e.preventDefault();
                window.print();
            }
            // Escape to close window
            if (e.key === "Escape") {
                window.close();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    // Fetch story data for print (premium-protected endpoint)
    const {
        data: storyData,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["story-print", storyId],
        queryFn: () => apiClient.getStoryForPrint(storyId),
        enabled: !!user && !loading,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: (failureCount, error) => {
            // Don't retry if it's a tier/permission error
            const errorMessage = error?.message || "";
            if (errorMessage.includes("premium") || errorMessage.includes("tier")) {
                return false;
            }
            return failureCount < 2;
        },
    });

    if (loading || isLoading) {
        return <StoryLoadingState />;
    }

    if (!user) {
        return <AuthRequired />;
    }

    if (error) {
        const errorMessage = error?.message || "";
        const isPremiumError = errorMessage.includes("premium") || errorMessage.includes("tier");

        return (
            <div className="min-h-screen bg-white flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                    <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-6" />
                    <h1 className="text-2xl font-bold mb-4 text-black">
                        {isPremiumError ? "Premium Feature Required" : "Story Not Found"}
                    </h1>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                        {isPremiumError
                            ? "Print Story is a premium feature. Upgrade to Plus to print and export your stories as PDFs."
                            : "This story doesn't exist or you don't have permission to view it."}
                    </p>
                    <p className="text-sm text-gray-500">Press Escape to close this window</p>
                </div>
            </div>
        );
    }

    if (!storyData) {
        return <StoryLoadingState />;
    }

    const { story, steps } = storyData;

    return (
        <div className="min-h-screen bg-white text-black">
            {/* Keyboard shortcut hint - only visible on screen */}
            <div className="no-print fixed top-2 right-2 text-xs text-gray-400 bg-white/90 px-2 py-1 rounded">
                Ctrl+P to print • Esc to close
            </div>

            {/* Story content - completely minimal */}
            <div className="max-w-2xl mx-auto px-6 py-8">
                {/* Title */}
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold mb-2 text-black">{story.title || "My Story Adventure"}</h1>
                    <p className="text-sm text-gray-600">{new Date(story.created_at).toLocaleDateString()}</p>
                </div>

                {/* Story Content */}
                <div className="space-y-6">
                    {steps.map((step) => (
                        <div key={step.id} className="break-inside-avoid">
                            <h2 className="text-lg font-semibold mb-3 text-black">Chapter {step.step_number}</h2>

                            <div className="mb-2">
                                <p className="text-base leading-relaxed text-black whitespace-pre-wrap">
                                    {step.story_text}
                                </p>
                            </div>

                            {step.drawing && (
                                <img
                                    src={step.drawing.image_data}
                                    alt={`Chapter ${step.step_number} drawing`}
                                    className="max-w-full mx-auto"
                                    style={{ maxHeight: "250px", height: "auto" }}
                                />
                            )}

                            {step.story_followup_text && (
                                <div className="mt-2">
                                    <p className="text-base leading-relaxed text-black whitespace-pre-wrap">
                                        {step.story_followup_text}
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Branded footer */}
                <div className="mt-8 pt-4 text-center border-t border-gray-200 story-footer">
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <Logo size="sm" className="logo" />
                        <span className="text-sm font-medium text-gray-700">StoryDoodle</span>
                    </div>
                    <p className="text-xs text-gray-500">Interactive storytelling for creative minds</p>
                </div>
            </div>

            {/* Print-specific styles */}
            <style>{`
                /* Clean print styles */
                @media print {
                    .no-print {
                        display: none !important;
                    }

                    body {
                        -webkit-print-color-adjust: exact;
                        color-adjust: exact;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: white !important;
                    }

                    .break-inside-avoid {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }

                    h1, h2, h3 {
                        break-after: avoid;
                        page-break-after: avoid;
                        color: #000 !important;
                    }

                    p {
                        color: #000 !important;
                    }

                    /* Story images - allow reasonable sizing */
                    img:not(.logo) {
                        max-width: 100% !important;
                        height: auto !important;
                        break-inside: avoid;
                        page-break-inside: avoid;
                        display: block;
                        margin: 0 auto;
                    }

                    /* Logo in footer - keep tiny for print */
                    .logo img {
                        height: 12px !important;
                        max-height: 12px !important;
                        width: auto !important;
                    }

                    /* Compact footer for print */
                    .story-footer {
                        page-break-inside: avoid;
                        font-size: 0.8em;
                        margin-top: 1rem !important;
                        padding-top: 0.5rem !important;
                        line-height: 1.2;
                    }

                    .story-footer .flex {
                        gap: 0.25rem !important;
                        margin-bottom: 0.25rem !important;
                    }

                    .story-footer span {
                        font-size: 0.75rem !important;
                    }

                    .story-footer p {
                        font-size: 0.6rem !important;
                        margin: 0 !important;
                    }

                    @page {
                        margin: 0.75in;
                        size: letter;
                    }
                }
            `}</style>
        </div>
    );
}
